import { productsService } from '@/services'
import { useDispatch } from 'react-redux'
import { errorActions, uiActions } from '@/redux/actions'
import { useCatalog } from '@/hooks'
import { useIntl } from 'react-intl'
import { CONFIGURATOR_ID, SUBFAMILY_ID } from '@/constants/ui.constants'
import { PRODUCT_CATEGORIES } from '@/constants/product.constants'
import TogglesV2 from '@domain/toggles/TogglesManager'

export default function useProduct() {
	const dispatch = useDispatch()
	const { locale } = useIntl()
	const { getSelectedCatalog } = useCatalog()

	const getProductAvailability = async (product, quantity = null, lang) => {
		if (quantity || product?.quantity) {
			try {
				const res = await productsService.getProductAvailability({
					lang: lang,
					product: {
						...product,
						quantity: quantity ? quantity : product.quantity
					}
				})
				return res.data[0].availability
			} catch (err) {
				dispatch(errorActions.setError(err))
			}
			return null
		}
	}

	const validateProduct = (projectId, product) => {

		const configuratorId = product?.configuratorType?.id
		const subFamilyId = product?.subFamily?.id

		switch (configuratorId) {
			case CONFIGURATOR_ID.CREA:
				dispatch(
					uiActions.navigateTo({
						path: `/project/${projectId}/product/${product._id}/crea-configurator/validate`
					})
				)
				break
			case CONFIGURATOR_ID.DRAWING:
				switch (subFamilyId) {
					case SUBFAMILY_ID.PLUSMINUS:
						dispatch(
							uiActions.navigateTo({
								path: `/project/${projectId}/product/${product._id}/plusminus-drawing-configurator/validate`
							})
						)
						break
					case SUBFAMILY_ID.STICKS:
						dispatch(
							uiActions.navigateTo({
								path: `/project/${projectId}/product/${product._id}/sticks-drawing-configurator/validate`
							})
						)
						break
				}
				break
			case CONFIGURATOR_ID.NODRAWING:
				switch (subFamilyId) {
					case SUBFAMILY_ID.PLUSMINUS:
						dispatch(
							uiActions.navigateTo({
								path: `/project/${projectId}/product/${product._id}/plusminus-nodrawing-configurator/validate`
							})
						)
						break
					case SUBFAMILY_ID.STICKS:
						dispatch(
							uiActions.navigateTo({
								path: `/project/${projectId}/product/${product._id}/sticks-nodrawing-configurator/validate`
							})
						)
						break
				}
				break

			case CONFIGURATOR_ID.COMPOSITOR:

				if (TogglesV2.isEnabled("validateComposingPendantsProduct")) {
					dispatch(
						uiActions.navigateTo({
							path: `/compositor?projectId=${projectId}&configId=${product.configId}&family=${product.subFamily.id}&catalog=${getSelectedCatalog()}&validating=true&productId=${product._id}`
						})
					)
					break
				}

			case CONFIGURATOR_ID.ATTRIBUTES_CONFIGURATOR:
				dispatch(
					uiActions.navigateTo({
						path: `/validate/${projectId}/${product._id}`
					})
				)
				break
		}
	}

	const export3D = (projectId, product) => {
		if (!product?._id || !projectId) {
			return
		}
		dispatch(
			uiActions.navigateTo({
				path: `/project/${projectId}/product/${product._id}/export`
			})
		)
	}

	const getImages = async (products = []) => {
		return new Promise((resolve) => {
			productsService.getProductImages({
				products: products,
				catalogId: getSelectedCatalog(),
				lang: locale
			})
				.then((response) => resolve(response.data))
				.catch(() => resolve(products))
		})
	}


	/**
	 * Checks if given product belongs to any of given collection names.
	 * Collections: ['Tube', 'Match', 'Plusminus', 'Sticks']
	 */
	const checkProductCollection = (product, collectionAlias = []) => {
		const collectionNames = typeof collectionAlias === 'string' ? [collectionAlias] : collectionAlias
		return collectionNames.includes(product?.subFamily?.description)
	}

	/**
	 * Checks if given product belongs to any of given category names.
	 * Categories: ['REGULAR', 'SPECIFIC_PLAYER', 'MODULAR', 'LG2', 'VIRTUAL', 'TOOLKIT']
	 */
	const checkProductCategory = (product, categoryAlias = []) => {
		const prdouctCategoryId = product?.category?.id
		const categories = typeof categoryAlias === 'string' ? [categoryAlias] : categoryAlias
		return categories.some((categoryName) => prdouctCategoryId === PRODUCT_CATEGORIES[categoryName])
	}

	return {
		checkProductCategory,
		checkProductCollection,
		getImages,
		getProductAvailability,
		validateProduct,
		export3D
	}
}
